import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmGuardianLinkFromInvite, previewGuardianInvite } from '../../api/guardian';
import { clearAuth } from '../../utils/auth';
import { AUTH_NOTICE_KEY } from '../../utils/sessionManager';
import Button from '../../components/common/Button';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { SelectInput } from '../../components/common/form';
import { apiErrorMessage, safeUserMessage } from '../../utils/apiError';

const RELATIONSHIP_KEYS = ['MOTHER', 'FATHER', 'GUARDIAN', 'GRANDPARENT', 'OTHER'];

const ConfirmGuardianLink = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const effectiveToken = useMemo(() => (token || '').trim(), [token]);

  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [relationship, setRelationship] = useState('GUARDIAN');

  useEffect(() => {
    clearAuth();
  }, []);

  useEffect(() => {
    if (!effectiveToken) {
      setLoadingPreview(false);
      return;
    }
    (async () => {
      try {
        const data = await previewGuardianInvite(effectiveToken);
        setPreview(data);
      } catch {
        setPreview({ valid: false, message: t('guardianPublic.invalidLink') });
      } finally {
        setLoadingPreview(false);
      }
    })();
  }, [effectiveToken, t]);

  const submit = async (accept) => {
    if (!effectiveToken) return;
    setSubmitting(true);
    try {
      const res = await confirmGuardianLinkFromInvite(effectiveToken, {
        relationship,
        accept,
      });
      const msg = accept
        ? t('guardianPublic.requestSubmitted')
        : t('guardianPublic.invitationDeclined');
      if (accept) {
        sessionStorage.setItem(AUTH_NOTICE_KEY, msg);
        navigate('/login', { replace: true });
      } else {
        alert(msg);
      }
    } catch (err) {
      alert(apiErrorMessage(err, t('guardianPublic.processFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPreview) {
    return (
      <div className="max-w-lg w-full bg-white p-8 rounded-xl shadow">
        <PageLoadingShell variant="bare" />
      </div>
    );
  }

  if (!effectiveToken || !preview?.valid || preview?.inviteType !== 'EXISTING_USER_LINK') {
    return (
      <div className="max-w-lg w-full bg-white p-8 rounded-xl shadow text-center">
        <h2 className="page-hero-title mb-3">{t('guardianPublic.inviteTitle')}</h2>
        <p className="text-red-600">{safeUserMessage(preview?.message, t('guardianPublic.invalidLink'))}</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg w-full bg-white p-6 sm:p-8 rounded-xl shadow">
      <h2 className="page-hero-title mb-2">{t('guardianPublic.confirmLink')}</h2>
      <p className="text-sm text-gray-600 mb-4">
        {preview.academyName && <span className="font-medium">{preview.academyName}</span>}
        {preview.studentDisplayName && (
          <>{t('guardianPublic.linkAccountFor')}<strong>{preview.studentDisplayName}</strong>.</>
        )}
      </p>
      {preview.invitedEmail && (
        <p className="text-sm text-gray-500 mb-4">{t('guardianPublic.accountEmail', { email: preview.invitedEmail })}</p>
      )}

      <SelectInput
        label={t('guardianPublic.relationship')}
        required
        value={relationship}
        onChange={(e) => setRelationship(e.target.value)}
        fieldClassName="mb-6"
      >
        {RELATIONSHIP_KEYS.map((key) => (
          <option key={key} value={key}>{t(`guardianPublic.relationships.${key}`)}</option>
        ))}
      </SelectInput>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button type="button" variant="primary" className="flex-1" disabled={submitting} onClick={() => submit(true)}>
          {submitting ? t('guardianPublic.submitting') : t('guardianPublic.acceptRequest')}
        </Button>
        <Button type="button" variant="secondary" className="flex-1" disabled={submitting} onClick={() => submit(false)}>
          {t('guardianPublic.decline')}
        </Button>
      </div>
    </div>
  );
};

export default ConfirmGuardianLink;
